import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  alpha,
  useTheme,
  Paper,
  CircularProgress,
  Alert,
  Slide,
} from "@mui/material";
import { Person, Phone, CheckCircle } from "@mui/icons-material";
import CustomerService from "../../../../services/CustomerService";
import { useUserWithCompany } from "hooks/useCompany";
import POSKeyboard from "../POSKeyboard";

/**
 * CustomerInfoForm Component
 * Form for collecting customer name and mobile number
 */
const CustomerInfoForm = ({
  customerName,
  setCustomerName,
  customerMobile,
  setCustomerMobile,
  specialInstructions,
  setSpecialInstructions,
}) => {
  const { companyId } = useUserWithCompany();
  const theme = useTheme();
  const [customerData, setCustomerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeKb, setActiveKb] = useState(null);

  // Debounced customer lookup when mobile number is complete
  useEffect(() => {
    let timeoutId;

    const lookupCustomer = async () => {
      if (customerMobile.length === 10) {
        setIsLoading(true);
        setError(null);

        try {
          const response = await CustomerService.getAll({
            mobile: customerMobile,
            companyId: companyId,
          });

          if (
            response?.data?.view?.data &&
            response.data.view.data.length > 0
          ) {
            const customer = response.data.view.data[0];
            setCustomerData(customer);
            // Auto-fill customer name if found
            if (customer.name && !customerName) {
              setCustomerName(customer.name);
            }
          } else {
            setCustomerData(null);
          }
        } catch (err) {
          console.error("Error fetching customer:", err);
          setError("Failed to lookup customer");
        } finally {
          setIsLoading(false);
        }
      } else {
        setCustomerData(null);
        setError(null);
      }
    };

    // Debounce the API call
    timeoutId = setTimeout(lookupCustomer, 500);

    return () => clearTimeout(timeoutId);
  }, [customerMobile]);

  // Reset customer data when mobile is cleared
  useEffect(() => {
    if (customerMobile.length === 0) {
      setCustomerData(null);
      setError(null);
    }
  }, [customerMobile]);

  // Get current keyboard value and onChange handler
  const getCurrentValue = () => {
    if (activeKb === "mobile") return customerMobile;
    if (activeKb === "name") return customerName;
    if (activeKb === "instructions") return specialInstructions;
    return "";
  };

  const handleKeyboardChange = (value) => {
    if (activeKb === "mobile") {
      // Only allow numbers for mobile
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setCustomerMobile(numericValue);
      }
    } else if (activeKb === "name") {
      setCustomerName(value);
    } else if (activeKb === "instructions") {
      setSpecialInstructions(value);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2.5,
          background: `linear-gradient(145deg, ${alpha("#fff", 1)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
          transition: "all 0.3s ease",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2.5,
            pb: 2.5,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Person sx={{ fontSize: 20, color: "white" }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: "0.95rem",
                mb: 0.3,
              }}
            >
              Customer Information
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                fontSize: "0.75rem",
                color: theme.palette.primary.main,
              }}
            >
              Mobile number is required
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Customer Mobile */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.secondary,
                mb: 0.8,
                display: "block",
              }}
            >
              Mobile Number
            </Typography>
            <TextField
              fullWidth
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="Enter 10 digit mobile number"
              value={customerMobile}
              onFocus={() => setActiveKb("mobile")}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ""); // Only numbers
                if (value.length <= 10) {
                  setCustomerMobile(value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1.5,
                    }}
                  >
                    <Phone
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 18,
                      }}
                    />
                  </Box>
                ),
                endAdornment: isLoading && (
                  <CircularProgress size={18} sx={{ mr: 0.5 }} />
                ),
              }}
              helperText={`${customerMobile.length}/10 digits`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 52,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[50], 0.8),
                  "& fieldset": {
                    borderWidth: 1.5,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  "&:hover fieldset": {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    borderWidth: 1.5,
                  },
                  "&.Mui-focused": {
                    bgcolor: "white",
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                    "& fieldset": {
                      borderWidth: 2,
                      borderColor: theme.palette.primary.main,
                    },
                  },
                },
                "& .MuiFormHelperText-root": {
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  mt: 0.8,
                  color: theme.palette.primary.main,
                },
              }}
            />
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Customer Name - Show only if customer not found OR customer found but name is empty */}
          {(!customerData || !customerData.name) && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.8,
                  display: "block",
                }}
              >
                Customer Name (Optional)
              </Typography>
              <TextField
                fullWidth
                type="text"
                inputMode="text"
                autoComplete="name"
                placeholder="Enter customer name"
                value={customerName}
                onFocus={() => setActiveKb("name")}
                onChange={(e) => setCustomerName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                      }}
                    >
                      <Person
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: 18,
                        }}
                      />
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 52,
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[50], 0.8),
                    "& fieldset": {
                      borderWidth: 1.5,
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                    "&:hover fieldset": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      borderWidth: 1.5,
                    },
                    "&.Mui-focused": {
                      bgcolor: "white",
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                      "& fieldset": {
                        borderWidth: 2,
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Existing Customer Info */}
          {customerData && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.success.light, 0.04)} 100%)`,
                border: `1.5px solid ${alpha(theme.palette.success.main, 0.25)}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`,
              }}
            >
              {/* Customer Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 3px 10px ${alpha(theme.palette.success.main, 0.3)}`,
                  }}
                >
                  <CheckCircle
                    sx={{
                      color: "white",
                      fontSize: 22,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.success.dark,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                    }}
                  >
                    Returning Customer
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      lineHeight: 1.3,
                    }}
                  >
                    {customerData.name}
                  </Typography>
                </Box>
              </Box>

              {/* Loyalty Stats */}
              {customerData.Loyalty && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  {/* Points */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "white",
                      border: `1.5px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      textAlign: "center",
                      boxShadow: `0 2px 6px ${alpha(theme.palette.warning.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.warning.main,
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {customerData.Loyalty.totalPoints}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: "0.7rem",
                      }}
                    >
                      Points
                    </Typography>
                  </Box>

                  {/* Orders */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "white",
                      border: `1.5px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      textAlign: "center",
                      boxShadow: `0 2px 6px ${alpha(theme.palette.info.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.info.main,
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {customerData.Loyalty.totalOrders}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: "0.7rem",
                      }}
                    >
                      Orders
                    </Typography>
                  </Box>

                  {/* Redeemed */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "white",
                      border: `1.5px solid ${alpha(theme.palette.error.main, 0.2)}`,
                      textAlign: "center",
                      boxShadow: `0 2px 6px ${alpha(theme.palette.error.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.error.main,
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {customerData.Loyalty.pointsRedeemed}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: "0.7rem",
                      }}
                    >
                      Redeemed
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Total Spent & Last Order */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Total Spent */}
                {customerData.Loyalty && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1.2,
                      borderRadius: 1.5,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        fontSize: "0.85rem",
                      }}
                    >
                      Total Spent
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.dark,
                      }}
                    >
                      Rs.{" "}
                      {parseFloat(customerData.Loyalty.totalSpent).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* Last Order Date */}
                {customerData.Loyalty?.lastOrderDate && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1.2,
                      borderRadius: 1.5,
                      bgcolor: "white",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        fontSize: "0.85rem",
                      }}
                    >
                      Last Order
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {new Date(
                        customerData.Loyalty.lastOrderDate,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Special Instructions */}
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.secondary,
                mb: 0.8,
                display: "block",
              }}
            >
              Special Instructions (Optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder="Add any special requests or instructions for this order..."
              value={specialInstructions}
              onFocus={() => setActiveKb("instructions")}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[50], 0.8),
                  "& fieldset": {
                    borderWidth: 1.5,
                    borderColor: alpha("#f59e0b", 0.2),
                  },
                  "&:hover fieldset": {
                    borderColor: alpha("#f59e0b", 0.4),
                    borderWidth: 1.5,
                  },
                  "&.Mui-focused": {
                    bgcolor: "white",
                    boxShadow: `0 0 0 3px ${alpha("#f59e0b", 0.1)}`,
                    "& fieldset": {
                      borderWidth: 2,
                      borderColor: "#f59e0b",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* POSKeyboard - Slide from bottom */}
      <Slide direction="up" in={activeKb !== null} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            p: 1.5,
            pb: 2,
            background: `linear-gradient(to top, rgba(10,12,24,0.85) 0%, transparent 100%)`,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <POSKeyboard
              value={getCurrentValue()}
              onChange={handleKeyboardChange}
              onClose={() => setActiveKb(null)}
              onEnter={() => setActiveKb(null)}
              numericOnly={activeKb === "mobile"}
            />
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default CustomerInfoForm;
